import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router';
import {
  schemaActionTerrainReseau,
  type MissionTerrainReseau,
  type TravailTerrainReseau,
} from '@eec/contrats';
import { requete } from '../api/client';
import { ErreurApi, messageErreur } from '../api/erreurs';
import { useSession } from '../session/FournisseurSession';
import { peut } from '../session/permissions';
import { enregistrerServiceWorker } from '../terrain/pwa';
import { preparerPhoto, transmettrePhoto } from './photos';
import { CarteMissionsTerrain } from './CarteMissionsTerrain';
import {
  contexteTerrain,
  creerCoffre,
  enregistrerCoffre,
  changerPhraseCoffre,
  archiverOperationsTerminees,
  exporterCoffre,
  restaurerCoffre,
  ouvrirCoffre,
  listerCoffres,
  ETATS_ACTION_TERRAIN,
  type CoffreOuvert,
  type DonneesTerrain,
} from './coffre';

export function PageTerrainReseau() {
  const { contexte } = useSession();
  const actif = contexte?.appartenance_active;
  const identite =
    contexte && actif?.organisation_id
      ? contexteTerrain({
          compte_id: contexte.compte.id,
          organisation_id: actif.organisation_id,
          appartenance_id: actif.id,
        })
      : null;
  const identiteRef = useRef(identite);
  identiteRef.current = identite;
  const precedent = useRef(identite);
  const [coffre, setCoffre] = useState<CoffreOuvert | null>(null);
  const [confirmationPhrase, setConfirmationPhrase] = useState('');
  const ouverture = useRef<string | null>(null);
  const verrouiller = useCallback(() => {
    ouverture.current = null;
    setCoffre(null);
    setConfirmationPhrase('');
  }, []);
  const afficher = useCallback((c: CoffreOuvert) => {
    ouverture.current = crypto.randomUUID();
    setCoffre(c);
  }, []);
  useEffect(
    () => () => {
      ouverture.current = null;
    },
    [],
  );
  const [coffres, setCoffres] = useState<string[]>([]);
  const [erreur, setErreur] = useState('');
  const [occupe, setOccupe] = useState(false);
  const [enLigne, setEnLigne] = useState(navigator.onLine);
  const [pret, setPret] = useState(Boolean(navigator.serviceWorker?.controller));
  useEffect(() => {
    void listerCoffres()
      .then(setCoffres)
      .catch((e) => setErreur(messageErreur(e)));
    const connexion = () => setEnLigne(navigator.onLine);
    window.addEventListener('online', connexion);
    window.addEventListener('offline', connexion);
    return () => {
      window.removeEventListener('online', connexion);
      window.removeEventListener('offline', connexion);
    };
  }, []);
  useEffect(() => {
    if (precedent.current !== identite && precedent.current !== null) verrouiller();
    if (coffre && identite && identite !== coffre.id) verrouiller();
    precedent.current = identite;
  }, [identite, coffre, verrouiller]);
  // Verrouillage de la copie déchiffrée après cinq minutes sans interaction.
  useEffect(() => {
    if (!coffre) return;
    let timer: ReturnType<typeof setTimeout>;
    const armer = () => {
      clearTimeout(timer);
      timer = setTimeout(verrouiller, 5 * 60_000);
    };
    armer();
    window.addEventListener('pointerdown', armer);
    window.addEventListener('keydown', armer);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('pointerdown', armer);
      window.removeEventListener('keydown', armer);
    };
  }, [coffre, verrouiller]);
  async function preparerPwa() {
    await enregistrerServiceWorker((e) => {
      if (e.pretHorsConnexion !== undefined) setPret(e.pretHorsConnexion);
      if (e.erreur) setErreur(e.erreur);
    });
  }
  async function telecharger(id: string) {
    const t = await requete<TravailTerrainReseau>('/interventions-reseau/terrain');
    if (identiteRef.current !== id || contexteTerrain(t) !== id)
      throw new Error('Le contexte a changé pendant le téléchargement');
    return t;
  }
  async function enregistrer(c: CoffreOuvert, d: DonneesTerrain) {
    const session = ouverture.current;
    const compteAvant = identiteRef.current;
    if (!session) throw new Error('Coffre verrouillé');
    const suivant = await enregistrerCoffre(c, d);
    if (
      session !== ouverture.current ||
      compteAvant !== identiteRef.current ||
      (identiteRef.current && identiteRef.current !== c.id)
    )
      throw new Error('Contexte modifié ; coffre verrouillé');
    setCoffre(suivant);
    return suivant;
  }
  async function synchroniser() {
    if (!coffre || identiteRef.current !== coffre.id) return;
    const session = ouverture.current;
    let courant = coffre;
    setErreur('');
    setOccupe(true);
    const bloques = new Set(
      courant.donnees.operations.filter((o) => o.etat === 'conflit').map((o) => o.mission_id),
    );
    try {
      const verifierPhoto = () => {
        if (identiteRef.current !== courant.id || session !== ouverture.current)
          throw new Error('Reconnectez le compte et le contexte du coffre');
      };
      for (const photo of courant.donnees.photos ?? []) {
        if (photo.etat === 'envoyee' || photo.etat === 'ecartee') continue;
        verifierPhoto();
        try {
          const piece_id = await transmettrePhoto(photo, verifierPhoto);
          courant = await enregistrer(courant, {
            ...courant.donnees,
            photos: courant.donnees.photos!.map((p) =>
              p.id === photo.id ? { ...p, etat: 'envoyee', erreur: null, piece_id } : p,
            ),
          });
        } catch (e) {
          verifierPhoto();
          bloques.add(photo.mission_id);
          courant = await enregistrer(courant, {
            ...courant.donnees,
            photos: courant.donnees.photos!.map((p) =>
              p.id === photo.id ? { ...p, etat: 'erreur', erreur: messageErreur(e) } : p,
            ),
          });
          if (!(e instanceof ErreurApi) || ![400, 403, 404, 409, 413].includes(e.statut)) throw e;
        }
      }
      for (const o of courant.donnees.operations) {
        if (o.etat !== 'attente' || bloques.has(o.mission_id)) continue;
        if (identiteRef.current !== courant.id || session !== ouverture.current)
          throw new Error('Reconnectez le compte et le contexte du coffre');
        try {
          await requete(`/interventions-reseau/${o.mission_id}/synchroniser`, {
            methode: 'POST',
            corps: { operation_id: o.id, action: o.action },
          });
          if (identiteRef.current !== courant.id || session !== ouverture.current)
            throw new Error('Coffre ou contexte verrouillé pendant la transmission');
          courant = await enregistrer(courant, {
            ...courant.donnees,
            operations: courant.donnees.operations.map((x) =>
              x.id === o.id ? { ...x, etat: 'envoyee', erreur: null } : x,
            ),
          });
        } catch (e) {
          if (e instanceof ErreurApi && [400, 403, 404, 409].includes(e.statut)) {
            bloques.add(o.mission_id);
            courant = await enregistrer(courant, {
              ...courant.donnees,
              operations: courant.donnees.operations.map((x) =>
                x.id === o.id ? { ...x, etat: 'conflit', erreur: messageErreur(e) } : x,
              ),
            });
          } else throw e;
        }
      }
      if (
        !courant.donnees.operations.some((o) => o.etat === 'attente' || o.etat === 'conflit') &&
        !courant.donnees.photos?.some((p) => p.etat === 'attente' || p.etat === 'erreur')
      )
        courant = await enregistrer(courant, {
          ...courant.donnees,
          travail: await telecharger(courant.id),
          missions_a_recharger: [],
        });
    } catch (e) {
      setErreur(messageErreur(e));
    } finally {
      setOccupe(false);
    }
  }
  const attente =
    coffre?.donnees.operations.filter((o) => o.etat === 'attente' || o.etat === 'conflit') ?? [];
  const expire = coffre ? Date.parse(coffre.donnees.travail.expire_le) <= Date.now() : false;
  const ouvertureVisible =
    coffre && (identite ? identite === coffre.id : precedent.current === null);
  return (
    <main className="eec-metier eec-terrain" style={{ maxWidth: 900, margin: 'auto', padding: 24 }}>
      <h1>Terrain — missions emportées</h1>
      <Link to={contexte ? '/interventions-reseau' : '/connexion'}>
        {contexte ? 'Retour aux missions connectées' : 'Se connecter pour synchroniser'}
      </Link>
      <p role="status">
        {enLigne ? 'Connexion réseau disponible' : 'Hors connexion'} ·{' '}
        {pret
          ? 'Application prête pour une ouverture hors connexion'
          : 'Préparation de l’application hors connexion requise'}
      </p>
      <p>
        Les données locales sont chiffrées avec votre phrase de déverrouillage, distincte du mot de
        passe de connexion. Elle n’est pas envoyée au serveur. Sans cette phrase, les opérations non
        synchronisées ne sont pas récupérables. Verrouillez le coffre après utilisation sur un
        appareil partagé.
      </p>
      {erreur && <p role="alert">{erreur}</p>}
      {!ouvertureVisible && identite && !coffres.includes(identite) && (
        <details>
          <summary>Restaurer une sauvegarde chiffrée</summary>
          <p>
            Choisissez une sauvegarde de ce même compte et de cette organisation. Aucun coffre
            existant ne sera remplacé. Utilisez la phrase qui protégeait le fichier lors de sa
            création ; les anciennes sauvegardes gardent leur ancienne phrase.
          </p>
          <form
            style={{ display: 'grid', gap: 12 }}
            onSubmit={async (e) => {
              e.preventDefault();
              const form = e.currentTarget;
              const f = new FormData(form);
              const fichier = f.get('sauvegarde');
              const id = identite;
              setErreur('');
              setOccupe(true);
              try {
                if (!(fichier instanceof File) || !fichier.size || fichier.size > 12 * 1024 * 1024)
                  throw new Error('Choisissez une sauvegarde de 12 Mio maximum');
                const c = await restaurerCoffre(
                  await fichier.text(),
                  String(f.get('phrase')),
                  id,
                  () => identiteRef.current === id,
                );
                if (identiteRef.current === id) afficher(c);
                setCoffres(await listerCoffres());
                form.reset();
                if (enLigne) await preparerPwa();
              } catch (e) {
                setErreur(messageErreur(e));
              } finally {
                setOccupe(false);
              }
            }}
          >
            <label>
              Fichier de sauvegarde
              <input
                name="sauvegarde"
                type="file"
                accept=".eec-coffre,.json,application/json"
                required
                disabled={occupe}
              />
            </label>
            <label>
              Phrase de la sauvegarde
              <input
                name="phrase"
                type="password"
                autoComplete="off"
                minLength={12}
                maxLength={128}
                required
                disabled={occupe}
              />
            </label>
            <button disabled={occupe}>Restaurer sans écraser de coffre</button>
          </form>
        </details>
      )}
      {!ouvertureVisible && (
        <>
          {identite &&
            peut(contexte, 'intervention', 'creer') &&
            enLigne &&
            !coffres.includes(identite) && (
              <form
                style={{ display: 'grid', gap: 12 }}
                onSubmit={async (e) => {
                  e.preventDefault();
                  const form = e.currentTarget;
                  const phrase = String(new FormData(form).get('phrase'));
                  const id = identite;
                  setErreur('');
                  setOccupe(true);
                  try {
                    const t = await telecharger(id);
                    const c = await creerCoffre(t, phrase);
                    if (identiteRef.current === id) afficher(c);
                    setCoffres(await listerCoffres());
                    form.reset();
                    await preparerPwa();
                  } catch (err) {
                    setErreur(messageErreur(err));
                  } finally {
                    setOccupe(false);
                  }
                }}
              >
                <h2>Préparer mes missions sur cet appareil</h2>
                <label>
                  Nouvelle phrase de déverrouillage{' '}
                  <input
                    name="phrase"
                    type="password"
                    minLength={12}
                    maxLength={128}
                    autoComplete="new-password"
                    required
                  />
                </label>
                <button disabled={occupe}>Télécharger et chiffrer mes missions</button>
              </form>
            )}
          {coffres.filter((id) => !identite || id === identite).length > 0 && (
            <form
              style={{ display: 'grid', gap: 12, marginTop: 24 }}
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const f = new FormData(form);
                const id = String(f.get('coffre'));
                setErreur('');
                setOccupe(true);
                try {
                  const c = await ouvrirCoffre(id, String(f.get('phrase')));
                  if (identiteRef.current && identiteRef.current !== id)
                    throw new Error('Ce coffre appartient à un autre compte ou contexte');
                  afficher(c);
                  form.reset();
                  if (enLigne) await preparerPwa();
                } catch (err) {
                  setErreur(messageErreur(err));
                } finally {
                  setOccupe(false);
                }
              }}
            >
              <h2>Ouvrir un coffre existant</h2>
              <label>
                Coffre local{' '}
                <select name="coffre">
                  {coffres
                    .filter((id) => !identite || id === identite)
                    .map((id) => (
                      <option key={id} value={id}>
                        Compte {id.split(':')[0]!.slice(-8)} · organisation{' '}
                        {id.split(':')[1]!.slice(-8)}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Phrase de déverrouillage{' '}
                <input
                  name="phrase"
                  type="password"
                  required
                  minLength={12}
                  maxLength={128}
                  autoComplete="off"
                />
              </label>
              <button disabled={occupe}>Déverrouiller le coffre</button>
            </form>
          )}
          {!identite && coffres.length === 0 && (
            <p>
              Connectez-vous en ligne puis préparez vos missions avant de partir sur le terrain.
            </p>
          )}
        </>
      )}
      {coffre && ouvertureVisible && (
        <>
          <button disabled={occupe} onClick={verrouiller}>
            Verrouiller le coffre
          </button>
          <button
            disabled={occupe}
            onClick={async () => {
              const session = ouverture.current;
              const compte = identiteRef.current;
              setErreur('');
              setOccupe(true);
              try {
                const texte = await exporterCoffre(coffre);
                if (!session || session !== ouverture.current || compte !== identiteRef.current)
                  throw new Error('Coffre verrouillé ou contexte modifié');
                const url = URL.createObjectURL(new Blob([texte], { type: 'application/json' }));
                const lien = document.createElement('a');
                lien.href = url;
                lien.download = `eec-terrain-${new Date().toISOString().replace(/[:.]/g, '-')}.eec-coffre`;
                lien.click();
                setTimeout(() => URL.revokeObjectURL(url), 1000);
              } catch (e) {
                setErreur(messageErreur(e));
              } finally {
                setOccupe(false);
              }
            }}
          >
            Télécharger une sauvegarde chiffrée
          </button>
          <p>
            La sauvegarde contient aussi les photos et archives. Conservez sa phrase séparément. Une
            copie antérieure garde sa phrase d’origine, même après un changement de phrase sur cet
            appareil. La restauration nécessite le même compte et la même organisation dans un
            navigateur sans coffre existant ; elle ne prolonge pas la validité des missions.
          </p>
          <details>
            <summary>Changer la phrase du coffre</summary>
            <p>
              Ce changement fonctionne hors connexion et conserve vos missions et opérations.
              Conservez la nouvelle phrase : elle sera nécessaire à la prochaine ouverture de ce
              coffre sur cet appareil.
            </p>
            <form
              style={{ display: 'grid', gap: 12 }}
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const f = new FormData(form);
                const nouvelle = String(f.get('nouvelle'));
                setErreur('');
                setConfirmationPhrase('');
                if (nouvelle !== f.get('confirmation')) {
                  setErreur('Les deux nouvelles phrases doivent être identiques');
                  return;
                }
                const session = ouverture.current;
                const compte = identiteRef.current;
                const autoriser = () =>
                  session !== null &&
                  session === ouverture.current &&
                  compte === identiteRef.current;
                setOccupe(true);
                try {
                  const suivant = await changerPhraseCoffre(
                    coffre,
                    String(f.get('actuelle')),
                    nouvelle,
                    autoriser,
                  );
                  form.reset();
                  if (autoriser()) {
                    setCoffre(suivant);
                    setErreur('');
                    setConfirmationPhrase(
                      'Phrase modifiée. Les missions et opérations sont conservées.',
                    );
                  }
                } catch (err) {
                  setErreur(messageErreur(err));
                } finally {
                  setOccupe(false);
                }
              }}
            >
              <label>
                Phrase actuelle
                <input
                  name="actuelle"
                  type="password"
                  autoComplete="off"
                  minLength={12}
                  maxLength={128}
                  required
                  disabled={occupe}
                />
              </label>
              <label>
                Nouvelle phrase
                <input
                  name="nouvelle"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  required
                  disabled={occupe}
                />
              </label>
              <label>
                Confirmer la nouvelle phrase
                <input
                  name="confirmation"
                  type="password"
                  autoComplete="new-password"
                  minLength={12}
                  maxLength={128}
                  required
                  disabled={occupe}
                />
              </label>
              <button disabled={occupe}>Enregistrer la nouvelle phrase</button>
              {confirmationPhrase && <p role="status">{confirmationPhrase}</p>}
            </form>
          </details>
          <p>
            Copie du {new Date(coffre.donnees.travail.telecharge_le).toLocaleString('fr-FR')} ·{' '}
            {attente.length} opération(s) à traiter.
          </p>
          {expire && (
            <p role="alert">
              La copie a plus de 24 heures. Les saisies sont suspendues ; conservez les opérations
              en attente, synchronisez puis rechargez vos missions.
            </p>
          )}
          {identite !== coffre.id && (
            <p>
              Pour transmettre, reconnectez le même compte et la même organisation. Votre phrase
              locale ne remplace pas l’authentification au serveur.
            </p>
          )}
          <button
            disabled={
              occupe ||
              !enLigne ||
              identite !== coffre.id ||
              !peut(contexte, 'intervention', 'creer')
            }
            onClick={() => void synchroniser()}
          >
            Synchroniser avec le serveur
          </button>
          <button
            disabled={occupe || !enLigne || identite !== coffre.id || attente.length > 0}
            onClick={async () => {
              setErreur('');
              setOccupe(true);
              try {
                await enregistrer(coffre, {
                  ...coffre.donnees,
                  travail: await telecharger(coffre.id),
                  missions_a_recharger: [],
                });
              } catch (e) {
                setErreur(messageErreur(e));
              } finally {
                setOccupe(false);
              }
            }}
          >
            Recharger mes missions
          </button>
          {coffre.donnees.travail.missions.length === 0 && (
            <p>Aucune mission active affectée à ce contexte.</p>
          )}
          <CarteMissionsTerrain missions={coffre.donnees.travail.missions} />
          <section aria-label="Photos du coffre">
            <h2>Photos des missions</h2>
            <p>
              Les photos sont réduites en JPEG puis chiffrées sur cet appareil. Maximum 5 photos de
              1 Mio chacune ; coffre total limité à 8 Mio. Les copies restent conservées après
              envoi. Une photo refusée bloque la transmission des actions de sa mission jusqu’à
              résolution.
            </p>
            <form
              style={{ display: 'grid', gap: 12 }}
              onSubmit={async (e) => {
                e.preventDefault();
                const form = e.currentTarget;
                const f = new FormData(form);
                const mission = coffre.donnees.travail.missions.find(
                  (m) => m.id === f.get('mission'),
                );
                const source = f.get('photo');
                const session = ouverture.current;
                const compte = identiteRef.current;
                setErreur('');
                setOccupe(true);
                try {
                  if (expire || !mission || ['VALIDATED', 'CANCELLED'].includes(mission.statut))
                    throw new Error('Mission indisponible pour une nouvelle photo');
                  if ((coffre.donnees.photos?.length ?? 0) >= 5)
                    throw new Error('Limite de 5 photos conservées dans ce coffre');
                  if (!(source instanceof File) || !source.size)
                    throw new Error('Choisissez une photo');
                  const photo = await preparerPhoto(source, mission.id, mission.titre);
                  if (!session || session !== ouverture.current || compte !== identiteRef.current)
                    throw new Error('Coffre verrouillé ou contexte modifié');
                  if (
                    coffre.donnees.photos?.some(
                      (p) => p.mission_id === mission.id && p.empreinte === photo.empreinte,
                    )
                  )
                    throw new Error('Cette photo est déjà conservée pour cette mission');
                  await enregistrer(coffre, {
                    ...coffre.donnees,
                    photos: [...(coffre.donnees.photos ?? []), photo],
                  });
                  form.reset();
                } catch (e) {
                  setErreur(messageErreur(e));
                } finally {
                  setOccupe(false);
                }
              }}
            >
              <label>
                Mission de la photo
                <select
                  name="mission"
                  aria-label="Mission de la photo"
                  required
                  disabled={occupe || expire}
                >
                  {coffre.donnees.travail.missions
                    .filter((m) => !['VALIDATED', 'CANCELLED'].includes(m.statut))
                    .map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.titre}
                      </option>
                    ))}
                </select>
              </label>
              <label>
                Photo à conserver
                <input
                  name="photo"
                  type="file"
                  capture="environment"
                  accept="image/jpeg,image/png,image/webp"
                  required
                  disabled={occupe || expire}
                />
              </label>
              <button disabled={occupe || expire || (coffre.donnees.photos?.length ?? 0) >= 5}>
                Conserver la photo chiffrée
              </button>
            </form>
            {(coffre.donnees.photos ?? []).map((p) => (
              <figure key={p.id}>
                <img
                  src={`data:image/jpeg;base64,${p.base64}`}
                  alt={`Photo conservée : ${p.mission}`}
                  style={{ maxWidth: '100%', maxHeight: 240, objectFit: 'contain' }}
                />
                <figcaption>
                  {p.mission} ·{' '}
                  {p.etat === 'envoyee'
                    ? 'Photo transmise'
                    : p.etat === 'ecartee'
                      ? 'Transmission écartée, photo conservée'
                      : p.etat === 'erreur'
                        ? 'Photo à reprendre'
                        : 'Photo en attente'}{' '}
                  · {Math.ceil(p.taille / 1024)} ko
                </figcaption>
                {p.erreur && p.etat !== 'ecartee' && (
                  <p role="alert">
                    {p.erreur} La photo reste conservée ; relancez la synchronisation après
                    résolution.
                  </p>
                )}
                {p.etat === 'erreur' && (
                  <button
                    disabled={occupe}
                    onClick={async () => {
                      setOccupe(true);
                      setErreur('');
                      try {
                        await enregistrer(coffre, {
                          ...coffre.donnees,
                          photos: coffre.donnees.photos!.map((x) =>
                            x.id === p.id ? { ...x, etat: 'ecartee' } : x,
                          ),
                        });
                      } catch (e) {
                        setErreur(messageErreur(e));
                      } finally {
                        setOccupe(false);
                      }
                    }}
                  >
                    Écarter cet envoi et conserver la photo
                  </button>
                )}
              </figure>
            ))}
          </section>
          {coffre.donnees.travail.missions.map((m) => (
            <Mission
              key={`${m.id}:${m.version}`}
              mission={m}
              donnees={coffre.donnees}
              bloque={
                expire ||
                occupe ||
                Boolean(coffre.donnees.missions_a_recharger?.includes(m.id)) ||
                coffre.donnees.operations.some((o) => o.mission_id === m.id && o.etat === 'conflit')
              }
              enregistrer={async (action) => {
                setErreur('');
                setOccupe(true);
                try {
                  if (coffre.donnees.operations.length >= 500)
                    throw new Error(
                      'Limite de 500 opérations dans la file active ; archivez les transmissions terminées pour poursuivre',
                    );
                  const regle = ETATS_ACTION_TERRAIN[action.action];
                  if (!regle?.depuis.includes(m.statut))
                    throw new Error('Action incompatible avec l’état local');
                  await enregistrer(coffre, {
                    ...coffre.donnees,
                    travail: {
                      ...coffre.donnees.travail,
                      missions: coffre.donnees.travail.missions.map((x) =>
                        x.id === m.id ? { ...x, statut: regle.vers, version: x.version + 1 } : x,
                      ),
                    },
                    operations: [
                      ...coffre.donnees.operations,
                      {
                        id: crypto.randomUUID(),
                        mission_id: m.id,
                        action,
                        etat: 'attente',
                        erreur: null,
                      },
                    ],
                  });
                } catch (e) {
                  setErreur(messageErreur(e));
                } finally {
                  setOccupe(false);
                }
              }}
            />
          ))}
          <h2>Historique local des transmissions</h2>
          <p>
            Archivez les transmissions envoyées ou écartées pour libérer la file active de 500
            opérations. Leur contenu reste dans ce coffre chiffré et consultable ci-dessous.
            L’archivage ne réduit pas sa taille totale, limitée à 8 Mio.
          </p>
          <button
            disabled={
              occupe ||
              !coffre.donnees.operations.some(
                (o) => o.etat === 'envoyee' || o.etat === 'abandonnee',
              )
            }
            onClick={async () => {
              setOccupe(true);
              setErreur('');
              try {
                await enregistrer(coffre, archiverOperationsTerminees(coffre.donnees));
              } catch (e) {
                setErreur(messageErreur(e));
              } finally {
                setOccupe(false);
              }
            }}
          >
            Archiver les transmissions terminées
          </button>
          <details>
            <summary>
              Archives conservées (
              {(coffre.donnees.archives ?? []).reduce((n, a) => n + a.operations.length, 0)}{' '}
              opérations)
            </summary>
            {(coffre.donnees.archives ?? []).map((a, i) => (
              <section key={i} aria-label={`Archive ${i + 1}`} style={{ overflowWrap: 'anywhere' }}>
                <h3>Archivage du {new Date(a.archive_le).toLocaleString('fr-FR')}</h3>
                <ol>
                  {a.operations.map((o) => (
                    <li key={o.id}>
                      <strong>
                        {o.action.action} · {o.etat}
                      </strong>
                      <p>
                        Mission : {o.mission_id} · Opération : {o.id}
                      </p>
                      {o.erreur && <p>{o.erreur}</p>}
                      <details>
                        <summary>Voir la saisie archivée</summary>
                        <p>{o.action.commentaire}</p>
                        <p>{o.action.observations}</p>
                        <p>{o.action.travaux}</p>
                        {o.action.consommations.length > 0 && (
                          <ul>
                            {o.action.consommations.map((c) => (
                              <li key={c.article_id}>
                                Article {c.article_id} : {c.quantite}
                              </li>
                            ))}
                          </ul>
                        )}
                      </details>
                    </li>
                  ))}
                </ol>
              </section>
            ))}
          </details>
          <p>
            Un conflit n’est jamais écrasé automatiquement. Écartez les tentatives de la mission
            concernée, rechargez la version du serveur puis ressaisissez la correction nécessaire.
            Les tentatives écartées restent consultables ci-dessous.
          </p>
          <ol>
            {coffre.donnees.operations.map((o) => (
              <li key={o.id}>
                <strong>
                  {o.action.action} · {o.etat}
                </strong>
                {o.erreur && <p>{o.erreur}</p>}
                <details>
                  <summary>Voir la saisie conservée</summary>
                  <p>{o.action.commentaire}</p>
                  <p>{o.action.observations}</p>
                  <p>{o.action.travaux}</p>
                </details>
                {o.etat === 'conflit' && (
                  <button
                    disabled={occupe}
                    onClick={async () => {
                      setOccupe(true);
                      setErreur('');
                      try {
                        await enregistrer(coffre, {
                          ...coffre.donnees,
                          missions_a_recharger: [
                            ...new Set([
                              ...(coffre.donnees.missions_a_recharger ?? []),
                              o.mission_id,
                            ]),
                          ],
                          operations: coffre.donnees.operations.map((x) =>
                            x.mission_id === o.mission_id && ['attente', 'conflit'].includes(x.etat)
                              ? {
                                  ...x,
                                  etat: 'abandonnee',
                                  erreur: 'Tentative écartée localement pour reprise manuelle',
                                }
                              : x,
                          ),
                        });
                      } catch (e) {
                        setErreur(messageErreur(e));
                      } finally {
                        setOccupe(false);
                      }
                    }}
                  >
                    Écarter les tentatives de cette mission
                  </button>
                )}
              </li>
            ))}
          </ol>
        </>
      )}
    </main>
  );
}
function Mission({
  mission: m,
  donnees,
  bloque,
  enregistrer,
}: {
  mission: MissionTerrainReseau;
  donnees: DonneesTerrain;
  bloque: boolean;
  enregistrer: (d: ReturnType<typeof schemaActionTerrainReseau.parse>) => Promise<void>;
}) {
  const actions = Object.entries(ETATS_ACTION_TERRAIN).filter(([, r]) =>
    r.depuis.includes(m.statut),
  );
  const [action, setAction] = useState(actions[0]?.[0] ?? '');
  const [pieces, setPieces] = useState<{ article_id: string; quantite: number }[]>([]);
  const [erreur, setErreur] = useState('');
  return (
    <article style={{ paddingBlock: 20, borderBottom: '1px solid currentColor' }}>
      <h2>{m.titre}</h2>
      <p>
        {m.incident_reference} · {m.localisation}
      </p>
      <p>{m.consignes}</p>
      <p>État local : {m.statut}. Une saisie hors connexion reste à confirmer par le serveur.</p>
      {erreur && <p role="alert">{erreur}</p>}
      {actions.length > 0 && (
        <form
          style={{ display: 'grid', gap: 12 }}
          onSubmit={async (e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            try {
              const d = schemaActionTerrainReseau.parse({
                action,
                version: m.version,
                commentaire: f.get('commentaire'),
                observations: f.get('observations') ?? '',
                travaux: f.get('travaux') ?? '',
                ...(action === 'rapport'
                  ? {
                      resultat: f.get('resultat'),
                      consommations: pieces,
                    }
                  : {}),
              });
              await enregistrer(d);
            } catch {
              setErreur('Vérifiez le commentaire, les champs du rapport et la quantité déclarée.');
            }
          }}
        >
          <label>
            Action terrain{' '}
            <select value={action} onChange={(e) => setAction(e.target.value)}>
              {actions.map(([k, r]) => (
                <option key={k} value={k}>
                  {r.libelle}
                </option>
              ))}
            </select>
          </label>
          <label>
            Commentaire terrain{' '}
            <textarea name="commentaire" required minLength={3} maxLength={4000} />
          </label>
          {action === 'rapport' && (
            <>
              <label>
                Observations terrain{' '}
                <textarea name="observations" required minLength={3} maxLength={4000} />
              </label>
              <label>
                Travaux réalisés sur le terrain{' '}
                <textarea name="travaux" required minLength={3} maxLength={4000} />
              </label>
              <label>
                Résultat terrain{' '}
                <select name="resultat">
                  <option value="RESTORED">Rétabli</option>
                  <option value="PARTIAL">Partiel</option>
                  <option value="NOT_RESTORED">Non rétabli</option>
                  <option value="TEMPORARY">Temporaire</option>
                </select>
              </label>
              {pieces.map((piece, index) => (
                <div key={index} style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                  <label>
                    Pièce consommée {index + 1}{' '}
                    <select
                      required
                      value={piece.article_id}
                      onChange={(e) =>
                        setPieces((p) =>
                          p.map((x, i) => (i === index ? { ...x, article_id: e.target.value } : x)),
                        )
                      }
                    >
                      <option value="">Choisir</option>
                      {donnees.travail.articles.map((a) => (
                        <option key={a.id} value={a.id}>
                          {a.reference} — {a.libelle}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Quantité consommée {index + 1}{' '}
                    <input
                      required
                      type="number"
                      min={1}
                      max={1000000}
                      step={1}
                      value={piece.quantite}
                      onChange={(e) =>
                        setPieces((p) =>
                          p.map((x, i) =>
                            i === index ? { ...x, quantite: Number(e.target.value) } : x,
                          ),
                        )
                      }
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => setPieces((p) => p.filter((_, i) => i !== index))}
                  >
                    Retirer cette ligne
                  </button>
                </div>
              ))}
              <button
                type="button"
                disabled={pieces.length >= 50 || bloque}
                onClick={() => setPieces((p) => [...p, { article_id: '', quantite: 1 }])}
              >
                Ajouter une consommation terrain
              </button>
            </>
          )}
          <button disabled={bloque}>Enregistrer sur cet appareil</button>
        </form>
      )}
    </article>
  );
}
